import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";
import {
  type SessionFileContribution,
  type SessionRecord,
} from "@/shared/domain/sessions";

import { sessionQueryKeys } from "./sessionQueryKeys";

export interface UseAddFilesToSessionVariables {
  sessionId: string;
  files: SessionFileContribution[];
}

/**
 * Add files to an existing session. On success the session's detail-scoped
 * caches (which include its files list) are invalidated, along with the
 * session lists, since the added files may have shifted the session's
 * aggregate bounds.
 */
export function useAddFilesToSession() {
  const { sessionService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<SessionRecord, Error, UseAddFilesToSessionVariables>({
    mutationFn: ({ sessionId, files }) =>
      sessionService.addFilesToSession(sessionId, files),
    onSuccess: (record) => {
      void queryClient.invalidateQueries({
        queryKey: sessionQueryKeys.detail(record.session_id),
      });
      void queryClient.invalidateQueries({
        queryKey: sessionQueryKeys.lists(),
      });
    },
  });
}
